import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCall } from '../context/CallContext';
import { useAuth } from '../context/AuthContext';
import { formatImageUrl } from '../utils/imageUtils';
import IncomingRing from '../assets/sounds/Crystalline_Dawn.mp3';
import { consultationAPI } from '../utils/api';

const IncomingCallOverlay = () => {
    const { callStatus, callData, answerCall, endCall } = useCall();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [show, setShow] = useState(false);
    const [step, setStep] = useState('incoming'); // incoming, verification (patient), disclaimer (doctor/clinic)
    const audioRef = useRef(null);

    const timeoutRef = useRef(null);

    useEffect(() => {
        if (callStatus === 'RINGING' && callData) {
            setShow(true);
            setStep('incoming');
            playRingtone();

            timeoutRef.current = setTimeout(() => {
                handleDecline('busy');
            }, 30000);
        } else {
            setShow(false);
            stopRingtone();
        }
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };
    }, [callStatus, callData]);

    const playRingtone = () => {
        try {
            if (!audioRef.current) {
                audioRef.current = new Audio(IncomingRing);
                audioRef.current.loop = true;
            }
            audioRef.current.play().catch(e => console.log('Audio play failed', e));

            // Vibrate if available
            if (navigator.vibrate) navigator.vibrate([1000, 500, 1000, 500]);
        } catch (e) {
            console.error('Ringtone error', e);
        }
    };

    const stopRingtone = () => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
        }
        if (navigator.vibrate) navigator.vibrate(0);
    };

    const handleAcceptClick = () => {
        if (user?.role === 'patient') {
            setStep('verification');
        } else if (['doctor', 'clinic', 'hospital', 'hospital_doctor', 'clinic_doctor'].includes(user?.role)) {
            setStep('disclaimer');
        } else {
            acceptCall();
        }
    };

    const acceptCall = () => {
        stopRingtone();
        answerCall();
        if (callData && callData.consultationId) {
            navigate('/consultation/' + callData.consultationId);
        }
        setShow(false);
        setStep('incoming');
    };

    const handleDecline = async (reason = 'rejected') => {
        stopRingtone();

        // hit the backend to mark as busy/rejected
        if (callData && callData.consultationId) {
            try {
                await consultationAPI.reject(callData.consultationId, { reason });
            } catch (err) {
                console.error('Failed to notify backend of rejection:', err);
            }
        }

        endCall(reason === 'busy' ? 'Busy' : 'Declined');
        setShow(false);
        setStep('incoming');
    };

    if (!show || !callData) return null;

    return (
        <div className="fixed inset-0 z-[9999] bg-gray-900 flex flex-col items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm"></div>

            {step === 'incoming' && (
                <div className="relative z-10 flex flex-col items-center animate-in fade-in duration-300 w-full max-w-md">
                    <div className="w-32 h-32 rounded-full border-4 border-gold p-1 mb-8 shadow-[0_0_30px_rgba(255,215,0,0.3)] animate-pulse">
                        <img
                            src={formatImageUrl(callData.callerAvatar || callData.caller?.profile_image) || "https://ui-avatars.com/api/?name=" + (callData.callerName || 'Unknown')}
                            alt="Caller"
                            className="w-full h-full rounded-full object-cover"
                        />
                    </div>
                    <h2 className="text-3xl font-bold text-white mb-2">{callData.callerName || 'Unknown Caller'}</h2>
                    <p className="text-gold text-lg mb-12 animate-pulse">Incoming {callData.type === 'audio' ? 'Voice' : 'Video'} Call...</p>

                    <div className="flex w-full justify-around px-10 mb-10">
                        <button
                            onClick={handleDecline}
                            className="bg-red-600 hover:bg-red-700 text-white w-20 h-20 rounded-full flex flex-col items-center justify-center shadow-lg transition-transform transform hover:-translate-y-2 active:scale-95"
                        >
                            <svg className="w-8 h-8 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                        </button>
                        <button
                            onClick={handleAcceptClick}
                            className="bg-green-500 hover:bg-green-600 text-white w-20 h-20 rounded-full flex flex-col items-center justify-center shadow-lg transition-transform transform hover:-translate-y-2 active:scale-95 animate-bounce"
                        >
                            <svg className="w-8 h-8 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path></svg>
                        </button>
                    </div>
                </div>
            )}

            {step === 'verification' && (
                <div className="relative z-10 bg-gray-800 rounded-2xl p-8 max-w-md w-full border border-gold/30 shadow-2xl text-center">
                    <h2 className="text-2xl font-bold text-gold mb-6">Verify Specialist Identity</h2>
                    <div className="w-48 h-48 mx-auto rounded-xl border-2 border-gray-600 overflow-hidden mb-6">
                        <img
                            src={formatImageUrl(callData.callerAvatar || callData.caller?.profile_image) || "https://ui-avatars.com/api/?name=" + (callData.callerName || 'Unknown')}
                            alt="Specialist Profile"
                            className="w-full h-full object-cover"
                        />
                    </div>
                    <p className="text-gray-300 mb-8 text-sm">
                        Please confirm that the specialist on the call matches this profile image before proceeding.
                    </p>
                    <div className="flex gap-4">
                        <button
                            onClick={handleDecline}
                            className="flex-1 bg-gray-700 text-light py-3 rounded-xl font-medium"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={acceptCall}
                            className="flex-1 bg-gold text-dark py-3 rounded-xl font-bold shadow-lg shadow-gold/20"
                        >
                            I Confirm
                        </button>
                    </div>
                </div>
            )}

            {step === 'disclaimer' && (
                <div className="relative z-10 bg-gray-800 rounded-2xl p-8 max-w-md w-full border border-red-500/30 shadow-2xl">
                    <h2 className="text-2xl font-bold text-red-500 mb-6 text-center">Professional Confirmation</h2>
                    <div className="bg-black/30 p-4 rounded-xl mb-8 border border-gray-700">
                        <ul className="text-gray-300 text-sm space-y-4">
                            <li className="flex gap-3">
                                <span className="text-gold">✓</span>
                                <span>I confirm that I provide services based on my expertise.</span>
                            </li>
                            <li className="flex gap-3">
                                <span className="text-gold">✓</span>
                                <span>I will not share any personal information like residential address or mobile number.</span>
                            </li>
                            <li className="flex gap-3">
                                <span className="text-gold">✓</span>
                                <span>All sessions will remain professional and strictly within the platform.</span>
                            </li>
                        </ul>
                    </div>
                    <div className="flex gap-4">
                        <button
                            onClick={handleDecline}
                            className="flex-1 bg-gray-700 text-light py-3 rounded-xl font-medium"
                        >
                            Decline
                        </button>
                        <button
                            onClick={acceptCall}
                            className="flex-1 bg-red-600 text-white py-3 rounded-xl font-bold shadow-lg shadow-red-600/20"
                        >
                            I Agree
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default IncomingCallOverlay;
